import React, { useState, useEffect } from 'react';
import { Button, Container, Row, Col, Card, Form, Modal } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import OrderClientForm from './OrderClientForm';
import EditCancelModal from '../Global/EditCancelModal';
import OrderProductListForm from './OrderProductListForm';
import PhotoUploader from '../Global/PhotoUploader';
import { createOrder, logError, getTimeSlots, getClientOrigins, getPaymentMethods, createLeadRegistry, updateLead } from '../../api';
import { isPropietario, isAdmin, isGestion, getSalesRepId as getAuthSalesRepId, getUseCustomLogo } from '../../services/AuthService';
import DatePicker from "react-datepicker"; // Importar DatePicker

const NewOrderForm = () => {
    const location = useLocation();
    const leadData = location.state?.leadData;
    const [client, setClient] = useState(null);
    const [selectedFurnitureType, setSelectedFurnitureType] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const [showGlobalCancelModal, setShowGlobalCancelModal] = useState(false);
    const [comments, setComments] = useState(''); // Comentarios externos
    const [internalComments, setInternalComments] = useState(''); // Comentarios internos
    const [prepaidAmount, setPrepaidAmount] = useState(null);
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [selectedBillingAddress, setSelectedBillingAddress] = useState(null);
    const [deliveryDate, setDeliveryDate] = useState(null); // Nuevo estado para la fecha de entrega
    const navigate = useNavigate();
    const [areAllProductsComplete, setAreAllProductsComplete] = useState(false);
    const [mileage, setMileage] = useState('');
    const [totalPrice, setTotalPrice] = useState(0); // cambiar de useState('') a useState(0)
    const [timeSlots, setTimeSlots] = useState([]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(''); // '' = Sin especificar
    const [photos, setPhotos] = useState({ photo1: null, photo2: null });
    const [clientOrigins, setClientOrigins] = useState([]);
    const [selectedClientOrigin, setSelectedClientOrigin] = useState('');
    const [paymentMethods, setPaymentMethods] = useState([]);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
    const [useCustomLogo, setUseCustomLogo] = useState(true);
    const [salesRepHasCustomLogo, setSalesRepHasCustomLogo] = useState(false);
    const [isImportant, setIsImportant] = useState(false);
    const [hasIncident, setHasIncident] = useState(false);
    const [showIncidentConfirmModal, setShowIncidentConfirmModal] = useState(false);

    // Prellenar datos del lead si están disponibles
    // useEffect(() => {
    //     if (leadData) {
    //         setComments(leadData.comments || '');
    //     }
    // }, [leadData]);

    // Carga de franjas horarias, orígenes de cliente y métodos de pago al montar
    useEffect(() => {
        const fetchData = async () => {
            try {
                const [slots, origins, methods] = await Promise.all([
                    getTimeSlots(),
                    getClientOrigins(),
                    getPaymentMethods()
                ]);
                setTimeSlots(slots);
                setClientOrigins(origins);
                setPaymentMethods(methods);
                setSalesRepHasCustomLogo(getUseCustomLogo());
            } catch (err) {
                console.error('Error al cargar datos:', err);
            }
        };
        fetchData();
    }, []);

    // Validación del formulario
    const isFormValid = () => {
        const isPrivilegedUser = isGestion() || isPropietario() || isAdmin();
        
        if (isPrivilegedUser) {
            return client && selectedAddress;
        }
        
        return client && selectedAddress && selectedFurnitureType.length > 0 && areAllProductsComplete;
    };

    const getValidationErrors = () => {
        const errors = [];
        const isPrivilegedUser = isGestion() || isPropietario() || isAdmin();
    
        if (!client) errors.push("Debes seleccionar un cliente.");
        if (!selectedAddress) errors.push("Debes seleccionar una dirección de entrega.");
        
        if (!isPrivilegedUser) {
            if (selectedFurnitureType.length === 0) errors.push("Debes agregar al menos un producto.");
            if (!areAllProductsComplete) errors.push("Todos los productos deben estar completos.");
        }
    
        return errors;
    };

    const handleProductsCompletionChange = (areComplete) => {
        setAreAllProductsComplete(areComplete);
    };

    const handleCreateOrderClick = () => {
        if (hasIncident) {
            setShowIncidentConfirmModal(true);
        } else {
            handleSaveOrder();
        }
    };

    const handleSaveOrder = async () => {
        const salesRepId = getAuthSalesRepId();
        if (!salesRepId) {
            alert('No se pudo obtener el ID del representante de ventas.');
            return;
        }

        const order = {
            ClientId: client?.id,
            AddressId: selectedAddress?.id,
            BillingAddressId: selectedBillingAddress?.id,
            SalesRepId: salesRepId,
            CarrierId: 0,
            OrderDate: new Date(),
            DeliveryDate: deliveryDate ?? tomorrow, 
            TotalAmount: totalPrice,
            Status: 'Pending',
            Items: selectedFurnitureType.map(product => ({
                UnitPrice: product.price,
                Quantity: product.quantity || 1,
                ProductPriceId: product.productPriceId,
                SelectedVariants: product.selectedVariants || [],
                ...(product.isSpecial && {
                    IsSpecial: true,
                    Description: product.description,
                    ProductTypeId: product.productTypeId ? Number(product.productTypeId) : null
                })
            })),
            ExternalComments: comments, // Comentarios externos
            InternalComments: internalComments, // Comentarios internos
            ...(mileage && { Mileage: mileage }), // Añade solo si mileage no está vacío
            ...(prepaidAmount && { alreadyPaid: parseFloat(prepaidAmount) }),
            TimeSlotId: Number(selectedTimeSlot),
            ...(selectedClientOrigin && { ClientOriginId: Number(selectedClientOrigin) }),
            ...(selectedPaymentMethod && { PaymentMethodId: Number(selectedPaymentMethod) }),
            ...(photos.photo1 && { InfoPhoto1Base64: photos.photo1 }),
            ...(photos.photo2 && { InfoPhoto2Base64: photos.photo2 }),
            UseCustomLogo: useCustomLogo,
            IsImportant: isImportant,
            HasIncident: hasIncident
        };
        
        try {
            const response = await createOrder(order);
            
            // Si el pedido se creó desde un lead, añadir registro y actualizar cliente
            if (leadData) {
                try {
                    await createLeadRegistry({
                        leadId: leadData.id,
                        RegistryTypeId: 3,
                        comments: `Pedido creado - ID: ${response.data.id}`,
                        salesRepId: salesRepId
                    });
                    
                    // No actualizar clientId aquí, ya se hace al guardar cliente
                } catch (registryError) {
                    console.error('Error al crear registro del lead:', registryError);
                }
            }
            
            navigate('/orders');
        } catch (error) {
            logError(error);
            console.error('Error al crear la orden:', error);
            alert('Hubo un error al crear la orden');
        }
    };

    const handleMileageChange = (newMileage) => {
        setMileage(newMileage);
        setIsDirty(true);
    };

    const handleCancelOrder = () => {
        if (!isDirty) {
            handleGoBack();
        } else {
            setShowGlobalCancelModal(true);
        }
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const handleCommentsChange = (e) => {
        setComments(e.target.value);
        setIsDirty(true);
    };

    const handleInternalCommentsChange = (e) => {
        setInternalComments(e.target.value);
        setIsDirty(true);
    };

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isDirty) {
                const message = 'Tienes cambios no guardados. ¿Estás seguro de que quieres salir?';
                e.returnValue = message;
                return message;
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isDirty]);

    // Establecer la fecha predeterminada como mañana
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    return (
        <Container className="order-form" data-testid="new-order-form">
            <Row className="mb-4">
                <Col>
                    <h1 className="text-center" data-testid="new-order-title">Nuevo pedido</h1>
                </Col>
            </Row>

            {/* Datos del cliente */}
            <Card className="mb-4" data-testid="client-form-card">
                <Card.Body>
                    {leadData && (
                        <div className="alert alert-info mb-3" data-testid="lead-info-alert">
                            <strong>📋 Creando pedido desde lead:</strong> {leadData.name} - {leadData.phone}
                        </div>
                    )}
                    <OrderClientForm
                        client={client}
                        setClient={setClient}
                        setSelectedAddress={setSelectedAddress}
                        setSelectedBillingAddress={setSelectedBillingAddress}
                        setIsDirty={setIsDirty}
                        isDirty={isDirty}
                        handleGoBack={handleGoBack}
                        leadData={leadData}
                    />
                </Card.Body>
            </Card>

            {/* Lista de productos */}
            <Card className="mb-4" data-testid="products-form-card">
                <Card.Body>
                    <OrderProductListForm
                        setSelectedFurnitureType={setSelectedFurnitureType}
                        setIsDirty={setIsDirty}
                        isDirty={isDirty}
                        onProductsCompletionChange={handleProductsCompletionChange}
                        onMileageChange={handleMileageChange}
                        onTotalPriceChange={setTotalPrice}
                        initialProducts={selectedFurnitureType}
                    />
                </Card.Body>
            </Card>

            {/* Pago adelantado */}
            <Card className="mb-4" data-testid="prepaid-amount-card">
                <Card.Body>
                    <Form.Group controlId="prepaidAmount">
                    <Form.Label>Señal (€)</Form.Label>
                    <Form.Control
                        type="number"
                        min="0"
                        step="1"
                        value={prepaidAmount ?? ''}
                        onChange={(e) => {
                            const value = e.target.value;
                            setIsDirty(true);
                            setPrepaidAmount(value === '' ? null : Number(value));
                        }}
                        placeholder="Introduce el importe ya pagado (opcional)"
                        data-testid="prepaid-amount-input"
                    />
                    </Form.Group>

                    {/* Mostrar restante a pagar si hay productos */}
                    {selectedFurnitureType.length > 0 && prepaidAmount !== null && (
                        <p className="mt-2">
                            Pendiente de pago: <strong>
                            {(totalPrice - prepaidAmount).toFixed(2)} €
                            </strong>
                        </p>
                    )}
                </Card.Body>
            </Card>

            {/* Comentarios externos */}
            <Card className="mb-4" data-testid="external-comments-card">
                <Card.Body>
                    <Form.Group controlId="orderComments">
                        <Form.Label>Comentarios externos</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={comments}
                            onChange={handleCommentsChange}
                            placeholder="Añade cualquier comentario adicional visible para el cliente..."
                            data-testid="external-comments-input"
                        />
                    </Form.Group>
                </Card.Body>
            </Card>

            {/* Comentarios internos */}
            <Card className="mb-4" data-testid="internal-comments-card">
                <Card.Body>
                    <Form.Group controlId="orderInternalComments">
                        <Form.Label>Comentarios internos</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            value={internalComments}
                            onChange={handleInternalCommentsChange}
                            placeholder="Añade notas internas visibles solo para el equipo..."
                            data-testid="internal-comments-input"
                        />
                    </Form.Group>
                </Card.Body>
            </Card>

           {/* Fecha de entrega y Franja Horaria en la misma línea */}
            <Card className="mb-4" data-testid="delivery-date-card">
                <Card.Body>
                    <Row className="g-3">
                    <Col xs={12} md={6}>
                        <Form.Group controlId="orderDeliveryDate">
                        <Form.Label>Fecha de entrega</Form.Label>
                        <DatePicker
                            selected={deliveryDate || tomorrow}
                            onChange={date => setDeliveryDate(date)}
                            minDate={today}
                            dateFormat="dd/MM/yyyy"
                            placeholderText="Seleccionar fecha"
                            className="form-control"
                            todayButton="Hoy"
                            showPopperArrow={false}
                            data-testid="delivery-date-picker"
                        />
                        </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                        <Form.Group controlId="timeSlotSelect">
                        <Form.Label>Horario de entrega (opcional)</Form.Label>
                        <Form.Control
                            as="select"
                            value={selectedTimeSlot}
                            onChange={(e) => {
                            setSelectedTimeSlot(e.target.value);
                            setIsDirty(true);
                            }}
                            data-testid="time-slot-select"
                        >
                            <option value="">Sin especificar</option>
                            {timeSlots.map((slot) => (
                            <option key={slot.valueId} value={slot.valueId}>
                                {slot.name}
                            </option>
                            ))}
                        </Form.Control>
                        </Form.Group>
                    </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Origen del Cliente y Método de Pago */}
            <Card className="mb-4" data-testid="client-origin-payment-card">
                <Card.Body>
                    <Row className="g-3">
                    <Col xs={12} md={6}>
                        <Form.Group controlId="clientOriginSelect">
                        <Form.Label>Origen del Cliente (opcional)</Form.Label>
                        <Form.Control
                            as="select"
                            value={selectedClientOrigin}
                            onChange={(e) => {
                            setSelectedClientOrigin(e.target.value);
                            setIsDirty(true);
                            }}
                            data-testid="client-origin-select"
                        >
                            <option value="">Sin especificar</option>
                            {clientOrigins.map((origin) => (
                            <option key={origin.valueId} value={origin.valueId}>
                                {origin.name}
                            </option>
                            ))}
                        </Form.Control>
                        </Form.Group>
                    </Col>

                    <Col xs={12} md={6}>
                        <Form.Group controlId="paymentMethodSelect">
                        <Form.Label>Método de Pago (opcional)</Form.Label>
                        <Form.Control
                            as="select"
                            value={selectedPaymentMethod}
                            onChange={(e) => {
                            setSelectedPaymentMethod(e.target.value);
                            setIsDirty(true);
                            }}
                            data-testid="payment-method-select"
                        >
                            <option value="">Sin especificar</option>
                            {paymentMethods.map((method) => (
                            <option key={method.valueId} value={method.valueId}>
                                {method.name}
                            </option>
                            ))}
                        </Form.Control>
                        </Form.Group>
                    </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Fotos informativas */}
            <PhotoUploader onPhotosChange={(newPhotos) => {
                setPhotos(newPhotos);
                setIsDirty(true);
            }} />

            {/* Pedido importante e incidencia */}
            <Card className="mb-4" data-testid="important-order-card">
                <Card.Body>
                    <Form.Group controlId="isImportant" className="mb-3">
                        <Form.Check
                            type="checkbox"
                            label="Marcar como pedido importante"
                            checked={isImportant}
                            onChange={(e) => {
                                setIsImportant(e.target.checked);
                                setIsDirty(true);
                            }}
                            data-testid="important-order-checkbox"
                        />
                    </Form.Group>
                    <Form.Group controlId="hasIncident">
                        <Form.Check
                            type="checkbox"
                            label="Crear como incidencia"
                            checked={hasIncident}
                            onChange={(e) => {
                                setHasIncident(e.target.checked);
                                setIsDirty(true);
                            }}
                            data-testid="has-incident-checkbox"
                        />
                    </Form.Group>
                </Card.Body>
            </Card>

            {/* Logo personalizado */}
            {salesRepHasCustomLogo && (
                <Card className="mb-4" data-testid="custom-logo-card">
                    <Card.Body>
                        <Form.Group controlId="useCustomLogo">
                            <Form.Check
                                type="checkbox"
                                label="Usar logo personalizado en este pedido"
                                checked={useCustomLogo}
                                onChange={(e) => {
                                    setUseCustomLogo(e.target.checked);
                                    setIsDirty(true);
                                }}
                                data-testid="use-custom-logo-checkbox"
                            />
                        </Form.Group>
                    </Card.Body>
                </Card>
            )}

            {/* Botones de acción */}
            <Row className="mt-4" data-testid="action-buttons-row">
                <Col xs={6} className="text-left">
                    <Button 
                        variant="success" 
                        onClick={handleCreateOrderClick}
                        disabled={!isFormValid()}
                        data-testid="create-order-button"
                    >
                        Crear pedido
                    </Button>
                </Col>
                <Col xs={6} className="text-right">
                    <Button variant="danger" onClick={handleCancelOrder} data-testid="cancel-order-button">
                        Cancelar Creación de pedido
                    </Button>
                </Col>
            </Row>

            {!isFormValid() && (
                <Card className="mb-3" data-testid="validation-errors-card">
                    <Card.Body>
                        <h5 className="text-danger" data-testid="validation-errors-title">No puedes crear el pedido aún:</h5>
                        <ul className="text-danger mb-0" data-testid="validation-errors-list">
                            {getValidationErrors().map((error, index) => (
                                <li key={index} data-testid={`validation-error-${index}`}>{error}</li>
                            ))}
                        </ul>
                    </Card.Body>
                </Card>
            )}

            {/* Modal de confirmación para cancelar */}
            {showGlobalCancelModal && isDirty && (
                <EditCancelModal
                    show={showGlobalCancelModal}
                    onConfirm={handleGoBack}
                    onCancel={() => setShowGlobalCancelModal(false)}
                />
            )}

            {/* Modal de confirmación para incidencia */}
            <Modal show={showIncidentConfirmModal} onHide={() => setShowIncidentConfirmModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar creación como incidencia</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>⚠️ Estás a punto de crear este pedido como incidencia. ¿Deseas continuar?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowIncidentConfirmModal(false)}>
                        Cancelar
                    </Button>
                    <Button variant="warning" onClick={() => {
                        setShowIncidentConfirmModal(false);
                        handleSaveOrder();
                    }}>
                        Sí, crear como incidencia
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default NewOrderForm;
